import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChartLine, Plus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function ProgressPage() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ metric: "", value: 0, unit: "", notes: "" });

  const load = async () => { const { data } = await api.get("/progress"); setLogs(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/progress", { ...form, value: Number(form.value) });
      setOpen(false); load();
      setForm({ metric: "", value: 0, unit: "", notes: "" });
      toast.success("Logged");
    } catch { toast.error("Log failed"); }
  };

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Data</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <ChartLine size={48} weight="duotone" className="text-[#FF3B30]" /> Progress
            </h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-log" className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
                <Plus size={18} weight="bold" className="mr-2" /> Log
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#262626] rounded-none">
              <DialogHeader><DialogTitle className="font-heading font-black uppercase text-2xl">New Log</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Metric</Label>
                  <Input required value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}
                    placeholder="e.g. Back Squat, 40m sprint, Body weight" data-testid="log-metric"
                    className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Value</Label>
                    <Input type="number" step="0.01" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                      data-testid="log-value" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Unit</Label>
                    <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      placeholder="kg, s, reps" data-testid="log-unit" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                </div>
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="log-notes" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                </div>
                <Button type="submit" data-testid="log-submit" className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">
                  Save Log
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-[#262626] bg-[#121212]">
          <div className="grid grid-cols-12 gap-4 p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">
            <div className="col-span-3">Date</div>
            <div className="col-span-4">Metric</div>
            <div className="col-span-2">Value</div>
            <div className="col-span-3">Notes</div>
          </div>
          {logs.length === 0 && <div className="p-12 text-center text-[#A3A3A3]" data-testid="empty-progress">No logs yet. Track your lifts, sprints and body weight.</div>}
          {logs.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#1C1C1C] last:border-0 text-sm" data-testid={`log-${l.id}`}>
              <div className="col-span-3 text-[#A3A3A3]">{new Date(l.logged_at).toLocaleDateString()}</div>
              <div className="col-span-4 font-semibold">{l.metric}</div>
              <div className="col-span-2 font-heading font-bold">{l.value} {l.unit}</div>
              <div className="col-span-3 text-[#A3A3A3] text-xs italic">{l.notes}</div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
